import ProductImage from '../components/ProductImage';
import React, { useEffect } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useCatalog } from '../hooks/useCatalog';
import CatalogGrid from '../components/CatalogGrid';
import Icon from '../components/Icon';
import { getImageUrl } from '../utils/image';

const categories = [
  {
    key: 'одежда',
    label: 'Одежда',
    note: 'Основа вашего образа',
    icon: 'shirt',
  },
  { key: 'обувь', label: 'Обувь', note: 'В вашем ритме', icon: 'shoe' },
  {
    key: 'аксессуары',
    label: 'Аксессуары',
    note: 'Детали решают',
    icon: 'bag',
  },
];
const filterNames = {
  category: 'Категория',
  subcategory: 'Подкатегория',
  brand: 'Бренд',
  size: 'Размер',
  color: 'Цвет',
  min_price: 'Цена от',
  max_price: 'Цена до',
  search: 'Поиск',
};

export default function HomePage({ onFilterToggle, filterOpen }) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const {
    products,
    total,
    isLoading,
    isError,
    refetch,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useCatalog();
  const activeFilters = [...params.entries()].filter(
    ([key]) => filterNames[key]
  );
  const showIntro = !activeFilters.length;
  const heroProduct = products.find((p) => p.imageUrl);
  const heroImage = heroProduct ? getImageUrl(heroProduct) : '';

  const title = params.get('search')
    ? `Результаты для «${params.get('search')}»`
    : params.get('subcategory') || params.get('category') || 'В вашем стиле';
  const removeFilter = (key) => {
    const next = new URLSearchParams(params);
    next.delete(key);
    if (key === 'category') next.delete('subcategory');
    setParams(next);
  };

  useEffect(() => {
    if (!products.length) return;
    const saved = sessionStorage.getItem('catalogScroll');
    if (!saved) return;
    try {
      const { url, scroll } = JSON.parse(saved);
      if (url === location.pathname + location.search)
        requestAnimationFrame(() => window.scrollTo(0, scroll));
    } catch {
      /* A stale scroll preference must not interrupt shopping. */
    } finally {
      sessionStorage.removeItem('catalogScroll');
    }
  }, [products, location.pathname, location.search]);

  return (
    <div className="market-home">
      {showIntro && (
        <>
          <section className="market-hero" aria-label="Откройте каталог">
            <div className="market-hero-copy">
              <span className="eyebrow">MAXMARKET · НА КАЖДЫЙ ДЕНЬ</span>
              <h1>
                Найдётся
                <br />
                то самое<span>.</span>
              </h1>
              <p>
                Вещи, в которых удобно быть собой.
                <br />
                Соберите свой образ — от основы до деталей.
              </p>
              <a className="btn btn-primary" href="#catalog">
                Найти своё <Icon name="arrow" size={18} />
              </a>
            </div>
            <div className="market-hero-visual" aria-hidden="true">
              <span className="hero-orbit" />
              {heroImage ? (
                <ProductImage product={heroProduct} src={heroImage} alt="Образ из каталога MaxMarket" width="100%" height="100%" loading="eager" />
              ) : (
                <Icon name="shirt" size={140} />
              )}
              <span className="hero-caption">
                <Icon name="bag" size={18} />
                Ваш стиль. Ваш выбор.
              </span>
            </div>
          </section>
          <div className="market-category-tiles">
            {categories.map((category) => (
              <Link
                key={category.key}
                to={`/?category=${category.key}`}
                className={`category-tile category-tile--${category.icon}`}
              >
                <span className="category-tile-icon">
                  <Icon name={category.icon} size={30} />
                </span>
                <span>
                  <strong>{category.label}</strong>
                  <small>{category.note}</small>
                </span>
                <Icon name="arrow" size={18} />
              </Link>
            ))}
          </div>
        </>
      )}
      <section
        id="catalog"
        className="catalog-section"
        aria-label="Каталог товаров"
      >
        <div className="catalog-heading">
          <div>
            <span className="eyebrow">КАТАЛОГ MAXMARKET</span>
            <h2>{title}</h2>
            <p aria-live="polite">
              {isLoading
                ? 'Подбираем товары…'
                : isError
                  ? 'Не удалось получить товары'
                  : `${total} товаров в каталоге`}
            </p>
          </div>
          <button
            className="catalog-filter-trigger btn btn-outline"
            onClick={onFilterToggle}
            aria-controls="catalog-filters"
            aria-expanded={filterOpen}
            aria-label="Фильтры"
          >
            <Icon name="filter" size={18} />
            {filterOpen ? 'Скрыть фильтры' : 'Фильтры'}
            {activeFilters.length > 0 && <b>{activeFilters.length}</b>}
          </button>
        </div>
        {activeFilters.length > 0 && (
          <div className="active-filters" aria-label="Применённые фильтры">
            {activeFilters.map(([key, value]) => (
              <button
                key={key}
                onClick={() => removeFilter(key)}
                aria-label={`Убрать фильтр ${filterNames[key]}: ${value}`}
              >
                {filterNames[key]}: {value}
                <Icon name="close" size={14} />
              </button>
            ))}
            <button className="filters-reset" onClick={() => setParams({})}>
              Сбросить всё
            </button>
          </div>
        )}
        {isLoading ? (
          <div
            className="catalog-skeleton"
            aria-label="Загрузка каталога"
            aria-busy="true"
          >
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i}>
                <span />
                <i />
                <i />
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="market-empty" role="alert">
            <Icon name="alert" size={30} />
            <h3>Каталог временно недоступен</h3>
            <p>Не удалось загрузить товары. Попробуйте ещё раз.</p>
            <button className="btn btn-primary" onClick={() => refetch()}>
              Повторить загрузку
            </button>
          </div>
        ) : !products.length ? (
          <div className="market-empty">
            <Icon name="search" size={30} />
            <h3>Пока ничего не нашли</h3>
            <p>Попробуйте другой запрос или уберите часть фильтров.</p>
            <button className="btn btn-primary" onClick={() => setParams({})}>
              Показать весь каталог
            </button>
          </div>
        ) : (
          <CatalogGrid
            products={products}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        )}
      </section>
    </div>
  );
}
